import { Alert, Group, SegmentedControl, Select, Stack, Table } from '@mantine/core';
import { IconTableOff } from '@tabler/icons-react';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import DateRangePicker from '../../../components/DateRangePicker/DateRangePicker';
import ErrorMessage from '../../../components/ErrorMessage';
import Spinner from '../../../components/Spinner';
import {
  buildCohortMatrix,
  cohortCellStyle,
  cohortValueExtent,
  COHORT_INTERVALS,
  COHORT_MEASURES,
  formatCohortCellValue,
  formatCohortLabel,
  periodColumnLabel,
} from '../../../helpers/cohortAnalysis';
import { dateToEndOfDay, dateToStartOfDay, daysAgoISODate, todayISODate } from '../../../helpers/dateUtils';
import getErrorMessage from '../../../helpers/getErrorMessage';
import { formatCount } from '../../../helpers/segmentPerformance';
import useFetch from '../../../helpers/useFetch';

// A year, rather than the 30 days the sibling tabs open on: the default interval
// is monthly, and a 30-day range would produce a single cohort with a single
// period - nothing to retain yet.
const INITIAL_PERIOD_DAYS = 365;

// Enough width per period column that a wide grid overflows its own scroll
// container rather than squeezing the cells. The two leading columns (the cohort
// and its size) are counted as three of these, the cohort label being the widest
// cell in the table.
const COLUMN_MIN_WIDTH = 90;

// The dates go out as full ISO datetimes at the local day boundaries, the same
// as AudiencePerformanceTab and FunnelsTab. This endpoint treats the range as
// real timestamps, so local midnight is exactly the boundary the user asked for.
//
// This is deliberately the opposite of the sibling RfmAnalysisTab, which must
// send bare YYYY-MM-DD because that endpoint reduces its params to day labels.
// Do not harmonise them - the RFM one would start answering for the wrong day.
const cohortsDebounced = AwesomeDebouncePromise(
  (fetch, accountId, domainId, startTs, endTs, interval, measure) =>
    fetch(
      `/api/admin/accounts/${accountId}/domains/${domainId}/cohorts/${encodeURIComponent(startTs)}/${encodeURIComponent(endTs)}?interval=${interval}&measure=${measure}`
    ),
  300
);

const CohortAnalysisTab = () => {
  const fetch = useFetch();
  const { accountId, domainId } = useParams();
  const currency = useSelector((state) => state.domain.domain.currency);

  const [startDate, setStartDate] = useState(daysAgoISODate(INITIAL_PERIOD_DAYS - 1));
  const [endDate, setEndDate] = useState(todayISODate());
  const [interval, setCohortInterval] = useState(COHORT_INTERVALS[0].value);
  const [measure, setMeasure] = useState(COHORT_MEASURES[0].value);
  const [view, setView] = useState('count');
  const [analysis, setAnalysis] = useState();
  const [error, setError] = useState();

  const percent = view === 'percent';

  useEffect(() => {
    let cancelled = false;
    setAnalysis(undefined);
    setError(undefined);

    cohortsDebounced(
      fetch,
      accountId,
      domainId,
      dateToStartOfDay(startDate, 'local'),
      dateToEndOfDay(endDate, 'local'),
      interval,
      measure
    )
      .then((data) => {
        if (!cancelled) setAnalysis(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      });

    return () => {
      cancelled = true;
    };
  }, [fetch, accountId, domainId, startDate, endDate, interval, measure]);

  const matrix = useMemo(() => buildCohortMatrix(analysis, percent), [analysis, percent]);
  const extent = useMemo(() => cohortValueExtent(matrix.rows), [matrix]);

  const controls = (
    <Group gap="sm" align="flex-end" wrap="wrap">
      <DateRangePicker
        preSetRanges
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        maxDate={todayISODate()}
      />
      <Select
        label="Interval"
        data={COHORT_INTERVALS}
        value={interval}
        onChange={(value) => setCohortInterval(value ?? interval)}
        allowDeselect={false}
        w={140}
      />
      <Select
        label="Measure"
        data={COHORT_MEASURES}
        value={measure}
        onChange={(value) => setMeasure(value ?? measure)}
        allowDeselect={false}
        w={140}
      />
      <SegmentedControl
        value={view}
        onChange={setView}
        data={[
          { value: 'count', label: '#' },
          { value: 'percent', label: '%' },
        ]}
      />
    </Group>
  );

  if (error) {
    return (
      <Stack gap="md" mt="md">
        {controls}
        <ErrorMessage message={getErrorMessage(error)} />
      </Stack>
    );
  }

  if (analysis === undefined) {
    return (
      <Stack gap="md" mt="md">
        {controls}
        <Spinner />
      </Stack>
    );
  }

  if (matrix.rows.length === 0) {
    return (
      <Stack gap="md" mt="md">
        {controls}
        <Alert icon={<IconTableOff size={16} />} color="gray" variant="light" title="No cohorts in this range">
          A cohort is the group of customers who made their first purchase in the same {interval}. There are no first
          purchases in the selected range, so there is nothing to follow over time yet.
        </Alert>
      </Stack>
    );
  }

  const cellText = (value) => formatCohortCellValue(value, { measure, percent, currency });

  return (
    <Stack gap="md" mt="md">
      {controls}

      <Table.ScrollContainer minWidth={COLUMN_MIN_WIDTH * (matrix.periodCount + 3)}>
        <Table withTableBorder withColumnBorders highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Cohort</Table.Th>
              <Table.Th>Cohort size</Table.Th>
              {Array.from({ length: matrix.periodCount }, (_, index) => (
                <Table.Th key={index}>{periodColumnLabel(index, interval)}</Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {matrix.rows.map((row) => (
              <Table.Tr key={row.cohort}>
                <Table.Td>{formatCohortLabel(row.cohort, interval)}</Table.Td>
                <Table.Td>{formatCount(row.size)}</Table.Td>
                {row.cells.map((value, index) => (
                  <Table.Td key={index} style={cohortCellStyle(value, extent)}>
                    {cellText(value)}
                  </Table.Td>
                ))}
              </Table.Tr>
            ))}
          </Table.Tbody>
          {/* The totals row carries no heatmap colour: it is a column sum, an
              order of magnitude above the cohort cells the scale is keyed to. */}
          <Table.Tfoot>
            <Table.Tr>
              <Table.Th>All cohorts</Table.Th>
              <Table.Th>{formatCount(matrix.totals.size)}</Table.Th>
              {matrix.totals.cells.map((value, index) => (
                <Table.Th key={index}>{cellText(value)}</Table.Th>
              ))}
            </Table.Tr>
          </Table.Tfoot>
        </Table>
      </Table.ScrollContainer>
    </Stack>
  );
};

export default CohortAnalysisTab;
